/**
 * CreateContentStrategyDialog — minimal viable form for launching a content
 * plan pipeline. Follows the CreateInsightsDialog pattern.
 *
 * Required fields (validated before submit):
 *   name, dateStart, dateEnd, targetPlatforms, cadence slots,
 *   primaryGoal, contentMixPreset, pillarMode, at least one audience id.
 *
 * On submit: calls ContentPlanApiClient.generateContentPlan() (fire-and-forget),
 * calls onSuccess with { taskId, strategyId } for the parent to track.
 *
 * The plan's 6-step wizard UI is deferred to a follow-up iteration — this
 * MVP form satisfies the E2E gate + the pipeline contract.
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Stack,
  TextField,
  Typography,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Slider,
  Divider,
  Button,
  Chip,
  Collapse,
  IconButton,
  Menu,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  BaseDialog,
  DialogHeader,
  DialogContent,
  DialogFooter,
} from '@modules/ui-components';
import {
  ContentPlanApiClient,
  type GenerateContentPlanRequest,
  type StrategyPreflightResponse,
} from '@modules/content-plan/services/ContentPlanApiClient';
import { ContentStrategyFirestoreService } from '@modules/content-plan/services/ContentStrategyFirestoreService';
import type {
  ContentMixPreset,
  ContentStrategy,
  ContentTopic,
  PlatformId,
  PrimaryGoal,
  StrategyPlatformCadence,
} from '@modules/content-plan/types';
import { useAuthor } from '@modules/content-plan/hooks/useAuthor';
import { useAuthorPlatforms } from '@modules/content-plan/hooks/useAuthorPlatforms';
import { useProjectDefaultModel } from '@modules/content-plan/hooks/useProjectDefaultModel';
import { AvailableModelSelector } from '@modules/ai-settings';
import { CompositeDataPicker } from '@modules/data-pickers';
import {
  StrategyGoalQuotaEditor,
  type GoalQuotaEntry,
} from '@modules/content-plan/components/sprint/StrategyGoalQuotaEditor';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import { useCompositeDataOrchestrator } from '@modules/strategy-blocks/hooks/useCompositeDataOrchestrator';
import { checkBacklogCoverage, sprintsToQuotaPlan } from '@modules/content-plan/utils/checkBacklogCoverage';
import { buildStrategyPreflightKey } from '@modules/content-plan/utils/strategyPreflightKey';
import { CONTENT_PLAN } from '@test-kit/selectors/content-plan';
import { PlatformCadenceFields } from '@modules/content-plan/components/platforms/PlatformCadenceFields';
import {
  buildStrategyCadencesFromAuthor,
  cadenceToFieldSlots,
  postsPerWeekMapFromCadences,
  postsPerWeekFromCadence,
  withFieldSlots,
} from '@modules/content-plan/utils/strategyCadence';
import {
  authorPlatformService,
  type SystemPlatformLite,
} from '@modules/content-plan/services/AuthorPlatformService';
import { AudienceBriefMultiPicker } from '@modules/content-plan/components/strategy/AudienceBriefMultiPicker';
import type { AudienceBriefRef } from '@modules/content-plan/types';

export interface CreateContentStrategyDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  userId: string;
  authorId: string | null;
  /** Strategy service — powers CompositeDataPicker's data loading (created once in ProjectView). */
  strategyService: StrategyService;
  /** Optional: existing backlog topics for live backlog-coverage panel */
  backlogTopics?: ContentTopic[];
  /** Optional: sprint quota plan (from Phase 0) for live coverage check */
  sprintQuotaPlan?: ReturnType<typeof sprintsToQuotaPlan>;
  /** Optional: initial selection paths (e.g. pre-selected audiences) */
  initialSelectionPaths?: string[];
  /** Optional: pre-enable one-shot mode (used when retrying
      a failed Stage B task from TaskErrorResolver → INSUFFICIENT_BACKLOG). */
  initialAutoFillBacklog?: boolean;
  onSuccess?: (params: { taskId: string; strategyId: string }) => void;
  onTaskStarted?: (taskId: string, description: string) => void;
}

type StrategyReadinessCoverage = {
  ok: boolean;
  gaps: Array<{ themeId: string; missingCount: number }>;
  overallCoverage: number;
} | null;

export const CreateContentStrategyDialog: React.FC<CreateContentStrategyDialogProps> = ({
  open,
  onClose,
  projectId,
  userId,
  authorId,
  strategyService,
  backlogTopics,
  sprintQuotaPlan,
  initialSelectionPaths,
  initialAutoFillBacklog = false,
  onSuccess,
  onTaskStarted,
}) => {
  const navigate = useNavigate();
  // workflow-golden-tower — B2B audience-explicit pipeline toggle.
  // 'b2c' (default) keeps the legacy run(ctx) one-shot path used by audience+task selection.
  // 'b2b' switches to the draft/approve gate that requires AudienceBriefRefs[].
  type StrategyPipelineMode = 'b2c' | 'b2b';
  const [pipelineMode, setPipelineMode] = useState<StrategyPipelineMode>('b2c');
  const [selectedAudienceBriefRefs, setSelectedAudienceBriefRefs] = useState<AudienceBriefRef[]>([]);
  const [audienceCoveragePolicy, setAudienceCoveragePolicy] = useState<'advisory' | 'strict'>('advisory');

  const [name, setName] = useState('');
  const [dateStart, setDateStart] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [dateEnd, setDateEnd] = useState<string>(() => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() + 28);
    return d.toISOString().slice(0, 10);
  });
  const [targetPlatforms, setTargetPlatforms] = useState<PlatformId[]>([]);
  const [platformsTouched, setPlatformsTouched] = useState(false);
  const [platformCadences, setPlatformCadences] = useState<StrategyPlatformCadence[]>([]);
  const [cadenceTouched, setCadenceTouched] = useState(false);
  const [expandedCadencePlatforms, setExpandedCadencePlatforms] = useState<PlatformId[]>([]);
  const [platformMenuAnchor, setPlatformMenuAnchor] = useState<HTMLElement | null>(null);
  const [primaryGoal, setPrimaryGoal] = useState<PrimaryGoal>('leads' as PrimaryGoal);
  const [contentMixPreset, setContentMixPreset] = useState<ContentMixPreset>('80_20' as ContentMixPreset);
  // Wave 2 — goal-quota editor state. Default-empty so editor can pre-fill from
  // SPRINT_TYPE_DEFAULT_GOAL_MIX once the user implies a primary sprint type.
  // Strategy-create payload includes this as a default that the planner
  // applies to all sprints' outputGoalQuotas (planner normalises against
  // sprint length).
  const [defaultGoalQuotas, setDefaultGoalQuotas] = useState<GoalQuotaEntry[]>([]);
  // Phase C — pillarMode dropped from wizard (managed via Pillar editor in
  // Creator tab). Field is still present in ContentStrategy schema; left
  // unset in the wizard's outgoing payload.
  const [creatorResearchEnabled, setCreatorResearchEnabled] = useState(false);
  // One-shot is the default strategy path: fill missing backlog topics if
  // needed and immediately assign approved topics into sprint calendar slots.
  const ONESHOT_LS_KEY = 'cp.strategy.oneShotMode';
  const [oneShotMode, setOneShotMode] = useState<boolean>(() => {
    if (initialAutoFillBacklog) return true;
    if (typeof window === 'undefined') return true;
    try {
      const stored = window.localStorage.getItem(ONESHOT_LS_KEY);
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });

  React.useEffect(() => {
    if (open && initialAutoFillBacklog) setOneShotMode(true);
  }, [open, initialAutoFillBacklog]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(ONESHOT_LS_KEY, oneShotMode ? 'true' : 'false');
    } catch {
      /* non-critical */
    }
  }, [oneShotMode]);
  const projectDefaultModel = useProjectDefaultModel(projectId);
  const [modelStrategy, setModelStrategy] = useState(projectDefaultModel.value);
  const [modelPlatformPost, setModelPlatformPost] = useState(projectDefaultModel.value);

  React.useEffect(() => {
    if (!open) return;
    setModelStrategy(projectDefaultModel.value);
    setModelPlatformPost(projectDefaultModel.value);
  }, [open, projectDefaultModel.value]);

  // L.4 — reactive slot buffer (Publication w/o topic_id allowed per arch invariant).
  const [reactiveSlotPercent, setReactiveSlotPercent] = useState(0);
  const [advancedExpanded, setAdvancedExpanded] = useState(false);
  const autoFillBacklog = oneShotMode;
  const autoAssignTopicsToSlots = oneShotMode;

  const [selectedPaths, setSelectedPaths] = useState<string[]>(initialSelectionPaths ?? []);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [preflight, setPreflight] = useState<(StrategyPreflightResponse & { requestKey: string }) | null>(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [systemPlatforms, setSystemPlatforms] = useState<SystemPlatformLite[]>([]);
  const [systemPlatformsLoading, setSystemPlatformsLoading] = useState(false);
  const [systemPlatformsLoaded, setSystemPlatformsLoaded] = useState(false);
  const [systemPlatformsError, setSystemPlatformsError] = useState<string | null>(null);

  const { author, loading: authorLoading } = useAuthor(projectId);
  const {
    platforms: authorPlatforms,
    loading: authorPlatformsLoading,
    error: authorPlatformsError,
  } = useAuthorPlatforms(projectId, author?.id ?? null);
  const authorActivePlatforms = useMemo(
    () => authorPlatforms
      .filter((platform) => platform.isActive !== false)
      .map((platform) => platform.id as PlatformId),
    [authorPlatforms],
  );
  const systemPlatformIds = useMemo(
    () => systemPlatforms
      .map((platform) => platform.platformId as PlatformId)
      .filter(Boolean),
    [systemPlatforms],
  );
  const systemPlatformById = useMemo(
    () => new Map(systemPlatforms.map((platform) => [platform.platformId, platform])),
    [systemPlatforms],
  );
  const availablePlatforms = useMemo(
    () => systemPlatformIds.filter((platform) => !targetPlatforms.includes(platform)),
    [systemPlatformIds, targetPlatforms],
  );

  const fallbackPostsForPlatform = React.useCallback(
    (platform: PlatformId) => {
      const systemPlatform = systemPlatformById.get(String(platform));
      const max = Number(systemPlatform?.optimalPostFrequency?.max);
      if (Number.isFinite(max) && max > 0) return Math.round(max);
      const min = Number(systemPlatform?.optimalPostFrequency?.min);
      if (Number.isFinite(min) && min > 0) return Math.round(min);
      return 1;
    },
    [systemPlatformById],
  );

  const buildCadenceForPlatform = React.useCallback(
    (platform: PlatformId) => {
      const authorPlatform = authorPlatforms.find((candidate) => candidate.id === platform);
      const systemPlatform = systemPlatformById.get(String(platform));
      return buildStrategyCadencesFromAuthor({
        platforms: [platform],
        authorPlatforms: authorPlatform
          ? [authorPlatform]
          : systemPlatform
            ? [systemPlatform as any]
            : [],
        fallbackPostsPerWeek: { [String(platform)]: fallbackPostsForPlatform(platform) },
      })[0];
    },
    [authorPlatforms, fallbackPostsForPlatform, systemPlatformById],
  );

  React.useEffect(() => {
    if (!open) return;
    setPlatformsTouched(false);
    setCadenceTouched(false);
    setTargetPlatforms([]);
    setPlatformCadences([]);
    setExpandedCadencePlatforms([]);
    setPlatformMenuAnchor(null);
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setSystemPlatformsLoading(true);
    setSystemPlatformsLoaded(false);
    setSystemPlatformsError(null);
    authorPlatformService.getSystemPlatforms()
      .then((platforms) => {
        if (cancelled) return;
        setSystemPlatforms(platforms);
        setSystemPlatformsLoaded(true);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setSystemPlatforms([]);
        setSystemPlatformsError(err?.message ?? 'Failed to load system platform catalog');
      })
      .finally(() => {
        if (!cancelled) setSystemPlatformsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  React.useEffect(() => {
    if (!open || platformsTouched || authorActivePlatforms.length === 0 || targetPlatforms.length > 0) return;
    setTargetPlatforms(authorActivePlatforms);
    setExpandedCadencePlatforms((current) => current.length > 0 ? current : authorActivePlatforms);
  }, [open, platformsTouched, authorActivePlatforms, targetPlatforms.length]);

  React.useEffect(() => {
    if (!open || cadenceTouched || targetPlatforms.length === 0) return;
    setPlatformCadences(targetPlatforms.map((platform) => buildCadenceForPlatform(platform)));
  }, [open, cadenceTouched, targetPlatforms, buildCadenceForPlatform]);

  // Shared composite data picker orchestrator — same one StrategyFormDialog uses.
  const orchestrator = useCompositeDataOrchestrator({
    projectId,
    strategyService,
    initialSelection: selectedPaths,
    selectAll: false,
    onSelectionChange: (d) => setSelectedPaths(d.selectedIds),
    open,
  });

  // ─── Live backlog coverage probe (display-only) ───────────────
  const backlogCoverage = useMemo(() => {
    if (!backlogTopics || backlogTopics.length === 0 || !sprintQuotaPlan) return null;
    return checkBacklogCoverage({
      eligibleTopics: backlogTopics,
      sprintQuotaPlan,
    });
  }, [backlogTopics, sprintQuotaPlan]);

  const effectivePlatformCadences = useMemo(() => {
    const cadenceByPlatform = new Map(platformCadences.map((cadence) => [cadence.platform, cadence]));
    return targetPlatforms.map((platform) => {
      const existing = cadenceByPlatform.get(platform);
      if (existing) return existing;
      return buildCadenceForPlatform(platform);
    });
  }, [targetPlatforms, platformCadences, buildCadenceForPlatform]);

  const postsPerWeekPerPlatform = useMemo(
    () => postsPerWeekMapFromCadences(effectivePlatformCadences),
    [effectivePlatformCadences],
  );
  const cadenceSlotsReady = useMemo(
    () => effectivePlatformCadences.every((cadence) => postsPerWeekFromCadence(cadence) > 0),
    [effectivePlatformCadences],
  );

  const canSubmit =
    name.trim().length > 0 &&
    targetPlatforms.length > 0 &&
    cadenceSlotsReady &&
    new Date(dateEnd) > new Date(dateStart);

  const preflightRequestKey = useMemo(
    () => buildStrategyPreflightKey({
      projectId,
      userId,
      authorId: authorId ?? '',
      name: name.trim() || 'Untitled strategy',
      dateStart,
      dateEnd,
      targetPlatforms,
      postsPerWeekPerPlatform,
      platformCadences: effectivePlatformCadences,
      primaryGoal,
      contentMixPreset,
      autoFillBacklog,
      autoAssignTopicsToSlots,
    }),
    [
      projectId,
      userId,
      authorId,
      name,
      dateStart,
      dateEnd,
      targetPlatforms,
      postsPerWeekPerPlatform,
      effectivePlatformCadences,
      primaryGoal,
      contentMixPreset,
      autoFillBacklog,
      autoAssignTopicsToSlots,
    ],
  );

  React.useEffect(() => {
    if (
      !open ||
      targetPlatforms.length === 0 ||
      !cadenceSlotsReady ||
      !dateStart ||
      !dateEnd ||
      new Date(dateEnd) <= new Date(dateStart)
    ) {
      setPreflight(null);
      setPreflightLoading(false);
      return;
    }
    const requestKey = preflightRequestKey;
    setPreflight((current) => current?.requestKey === requestKey ? current : null);
    setPreflightLoading(true);
    let cancelled = false;
    const timer = window.setTimeout(() => {
      const client = new ContentPlanApiClient();
      client.strategyPreflight({
        projectId,
        userId,
        authorId: authorId ?? '',
        name: name.trim() || 'Untitled strategy',
        dateStart,
        dateEnd,
        targetPlatforms,
        postsPerWeekPerPlatform,
        platformCadences: effectivePlatformCadences,
        primaryGoal,
        contentMixPreset,
        autoFillBacklog,
        autoAssignTopicsToSlots,
      })
        .then((result) => {
          if (!cancelled) setPreflight(result.success ? { ...result, requestKey } : null);
        })
        .catch(() => {
          if (!cancelled) setPreflight(null);
        })
        .finally(() => {
          if (!cancelled) setPreflightLoading(false);
        });
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    open,
    projectId,
    userId,
    authorId,
    name,
    dateStart,
    dateEnd,
    targetPlatforms,
    cadenceSlotsReady,
    postsPerWeekPerPlatform,
    effectivePlatformCadences,
    primaryGoal,
    contentMixPreset,
    autoFillBacklog,
    autoAssignTopicsToSlots,
    preflightRequestKey,
  ]);

  const updatePlatformCadence = (
    platform: PlatformId,
    updater: (cadence: StrategyPlatformCadence) => StrategyPlatformCadence,
  ) => {
    setCadenceTouched(true);
    setPlatformCadences((current) => {
      const existing = current.find((cadence) => cadence.platform === platform)
        ?? effectivePlatformCadences.find((cadence) => cadence.platform === platform);
      if (!existing) return current;
      const next = updater(existing);
      if (current.some((cadence) => cadence.platform === platform)) {
        return current.map((cadence) => (cadence.platform === platform ? next : cadence));
      }
      return [...current, next];
    });
  };

  const currentPreflight = preflight?.requestKey === preflightRequestKey ? preflight : null;
  const readinessCoverage: StrategyReadinessCoverage = currentPreflight
    ? {
        ok: currentPreflight.coverage.ok,
        gaps: currentPreflight.coverage.gaps,
        overallCoverage: currentPreflight.overallCoverage,
      }
    : (preflightLoading || !backlogCoverage
        ? null
        : {
            ok: backlogCoverage.ok,
            gaps: backlogCoverage.gaps.map((gap) => ({
              themeId: gap.themeId,
              missingCount: gap.missingCount,
            })),
            overallCoverage: backlogCoverage.overallCoverage,
          });
  const estimatedTopicsToGenerate: number = currentPreflight?.estimatedTopicsToGenerate
    ?? (readinessCoverage && !readinessCoverage.ok
      ? readinessCoverage.gaps.reduce((sum, gap) => sum + gap.missingCount, 0)
      : 0);

  const addPlatform = (p: PlatformId) => {
    if (targetPlatforms.includes(p)) {
      setExpandedCadencePlatforms((current) => current.includes(p) ? current : [...current, p]);
      setPlatformMenuAnchor(null);
      return;
    }
    setPlatformsTouched(true);
    setCadenceTouched(true);
    setTargetPlatforms((prev) => [...prev, p]);
    setPlatformCadences((current) => {
      if (current.some((cadence) => cadence.platform === p)) return current;
      return [...current, buildCadenceForPlatform(p)];
    });
    setExpandedCadencePlatforms((current) => current.includes(p) ? current : [...current, p]);
    setPlatformMenuAnchor(null);
  };

  const removePlatform = (p: PlatformId) => {
    setPlatformsTouched(true);
    setCadenceTouched(true);
    setTargetPlatforms((prev) => prev.filter((x) => x !== p));
    setPlatformCadences((current) => current.filter((cadence) => cadence.platform !== p));
    setExpandedCadencePlatforms((current) => current.filter((platform) => platform !== p));
  };

  const toggleCadencePlatform = (p: PlatformId) => {
    setExpandedCadencePlatforms((current) =>
      current.includes(p)
        ? current.filter((platform) => platform !== p)
        : [...current, p],
    );
  };

  /**
   * Create an empty strategy shell — skips the AI pipeline entirely.
   *
   * Saves a minimal ContentStrategy through the protected backend via
   * ContentStrategyFirestoreService. The strategy lands with status='draft'
   * and no sprints/posts; the user populates it manually from the Calendar
   * tab (publications) or the Strategies drill-down (sprints + topics).
   *
   * Exists as an escape hatch from the autoFillBacklog / theme-coverage
   * validator path: when the backlog can't be satisfied and the user just
   * wants to hand-author a plan, they still need a ContentStrategy anchor
   * for publications + cadence to hang off.
   */
  const handleCreateBlank = async () => {
    if (!canSubmit) return;
    setError(null);
    setIsSubmitting(true);
    try {
      const strategyId = `cs_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
      const nowIso = new Date().toISOString();
      const strategy: ContentStrategy = {
        id: strategyId,
        projectId,
        authorId: authorId ?? undefined,
        name: name.trim(),
        status: 'draft' as ContentStrategy['status'],
        targetPlatforms,
        dateStart: dateStart as unknown as ContentStrategy['dateStart'],
        dateEnd: dateEnd as unknown as ContentStrategy['dateEnd'],
        primaryGoal,
        contentMixPreset,
        // pillarMode omitted — Phase C deprecation; ContentStrategy schema
        // still has the field but it's no longer driven by the wizard.
        postsPerWeekPerPlatform: postsPerWeekPerPlatform as ContentStrategy['postsPerWeekPerPlatform'],
        creatorResearchEnabled,
        createdAt: nowIso as unknown as ContentStrategy['createdAt'],
        updatedAt: nowIso as unknown as ContentStrategy['updatedAt'],
      };
      (strategy as any).platformCadences = effectivePlatformCadences;
      const service = new ContentStrategyFirestoreService();
      await service.saveStrategy(projectId, strategy);
      onTaskStarted?.(`blank_${strategyId}`, `Manual blank strategy: ${name}`);
      onSuccess?.({ taskId: `blank_${strategyId}`, strategyId });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * workflow-golden-tower B2B path — create a DRAFT strategy with
   * `approvalStage='draft_pending_approval'` and navigate the user to the
   * review screen. Differs from handleSubmit() by:
   *   1. requiring at least one AudienceBriefRef (no B2C audience/task selection),
   *   2. calling createStrategyDraft (B1 only — no autofill / sprint planner),
   *   3. navigating to /project/:projectId/content-plan/strategy/:strategyId/review.
   */
  const handleSubmitB2BDraft = async () => {
    if (!canSubmit) return;
    if (selectedAudienceBriefRefs.length === 0) {
      setError('Pick at least one audience brief before generating a draft.');
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      const ids = orchestrator.aiSourceDataIds;
      const client = new ContentPlanApiClient();
      const draftRequest: GenerateContentPlanRequest & {
        selectedAudienceBriefRefs: AudienceBriefRef[];
        audienceCoveragePolicy?: 'advisory' | 'strict';
      } = {
        projectId,
        userId,
        authorId: authorId ?? '',
        name: name.trim(),
        dateStart,
        dateEnd,
        targetPlatforms,
        postsPerWeekPerPlatform,
        platformCadences: effectivePlatformCadences,
        primaryGoal,
        sourceData: orchestrator.hydratedData,
        sourceDataIds: {
          audienceIds: ids.audienceIds,
          taskIds: ids.taskIds,
          studyCaseIds: ids.studyCaseIds ?? [],
          reviewIds: ids.reviewIds ?? [],
          ratingIds: ids.ratingIds ?? [],
          bigNumberIds: ids.bigNumberIds ?? [],
          insightIds: ids.insightIds ?? [],
        },
        contentMixPreset,
        creatorResearch: { enabled: creatorResearchEnabled },
        models: { strategy: modelStrategy, platformPost: modelPlatformPost },
        defaultGoalQuotas: defaultGoalQuotas.length > 0 ? defaultGoalQuotas : undefined,
        selectedAudienceBriefRefs,
        audienceCoveragePolicy,
      } as any;
      const response = await client.createStrategyDraft(draftRequest);
      if (!response.success) throw new Error(response.error ?? 'Draft generation failed');
      onTaskStarted?.(response.taskId, `Generating draft strategy: ${name}`);
      onSuccess?.({ taskId: response.taskId, strategyId: response.strategyId });
      onClose();
      // Navigate to the dedicated review screen. The route is registered
      // in frontend/src/App.jsx and gated behind the same auth as
      // /project/:projectId/content-plan.
      const params = new URLSearchParams();
      if (authorId) params.set('author', authorId);
      const suffix = params.toString() ? `?${params.toString()}` : '';
      navigate(`/project/${projectId}/content-plan/strategy/${response.strategyId}/review${suffix}`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError(null);
    setIsSubmitting(true);
    try {
      const ids = orchestrator.aiSourceDataIds;
      const client = new ContentPlanApiClient();
      const request: GenerateContentPlanRequest & {
        autoFillBacklog?: boolean;
        autoFillMaxCount?: number;
        autoAssignTopicsToSlots?: boolean;
        reactiveSlotPercent?: number;
      } = {
        projectId,
        userId,
        authorId: authorId ?? '',
        name: name.trim(),
        dateStart,
        dateEnd,
        targetPlatforms,
        postsPerWeekPerPlatform,
        platformCadences: effectivePlatformCadences,
        primaryGoal,
        sourceData: orchestrator.hydratedData,
        sourceDataIds: {
          audienceIds: ids.audienceIds,
          taskIds: ids.taskIds,
          studyCaseIds: ids.studyCaseIds ?? [],
          reviewIds: ids.reviewIds ?? [],
          ratingIds: ids.ratingIds ?? [],
          bigNumberIds: ids.bigNumberIds ?? [],
          insightIds: ids.insightIds ?? [],
        },
        contentMixPreset,
        // pillarMode omitted — Phase C deprecation, see Pillar editor in Creator tab.
        creatorResearch: { enabled: creatorResearchEnabled },
        models: { strategy: modelStrategy, platformPost: modelPlatformPost },
        autoFillBacklog,
        autoAssignTopicsToSlots,
        reactiveSlotPercent,
        // Wave 2 — default goal-quota mix that the planner uses as a starting
        // point for sprint.sprintContract.outputGoalQuotas. Empty array means
        // planner derives defaults from sprintType.
        defaultGoalQuotas: defaultGoalQuotas.length > 0 ? defaultGoalQuotas : undefined,
      } as any;
      // v2 staged pipeline — call Stage B endpoint directly
      const response = await client.createStrategy(request);
      if (!response.success) throw new Error(response.error ?? 'Pipeline failed');
      onTaskStarted?.(response.taskId, `Creating content strategy: ${name}`);
      onSuccess?.({ taskId: response.taskId, strategyId: response.strategyId });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      data-testid={CONTENT_PLAN.DIALOG}
      sx={{
        width: { xs: 'calc(100vw - 24px)', md: 1120 },
        maxWidth: 'calc(100vw - 24px)',
        height: { xs: 'calc(100vh - 24px)', md: 'min(880px, calc(100vh - 48px))' },
        maxHeight: 'calc(100vh - 24px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <DialogHeader title="Create Content Strategy" onClose={onClose} />
      <DialogContent maxHeight="none" sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Stack spacing={3} sx={{ py: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {authorPlatformsError && (
            <Alert severity="error">{authorPlatformsError.message}</Alert>
          )}
          {(authorLoading || authorPlatformsLoading) && targetPlatforms.length === 0 && (
            <Alert severity="info">Loading author platforms…</Alert>
          )}
          {!authorLoading && !authorPlatformsLoading && authorActivePlatforms.length === 0 && targetPlatforms.length === 0 && (
            <Alert severity="warning">
              No active author platforms configured. Add an author platform or explicitly select a platform.
            </Alert>
          )}

          {/* workflow-golden-tower — pipeline mode toggle.
              B2C path: legacy createStrategy() (one-shot, immediate planning).
              B2B path: createStrategyDraft() → review screen → approveStrategy(). */}
          <Box data-testid="strategy-pipeline-mode-toggle">
            <Typography variant="subtitle2" gutterBottom>Strategy mode</Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={pipelineMode}
              onChange={(_, next) => { if (next) setPipelineMode(next); }}
              aria-label="Strategy pipeline mode"
            >
              <ToggleButton value="b2c" data-testid="strategy-mode-b2c">
                B2C (audiences + tasks)
              </ToggleButton>
              <ToggleButton value="b2b" data-testid="strategy-mode-b2b">
                B2B (audience briefs · draft / approve)
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              {pipelineMode === 'b2b'
                ? 'Select B2B audience briefs from insights analyses. The strategy is generated as a draft, then you review and approve.'
                : 'Pick audiences/tasks/insights and run the one-shot planner immediately. Best for B2C catalogs.'}
            </Typography>
          </Box>

          <TextField
            label="Strategy name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            inputProps={{ 'data-testid': CONTENT_PLAN.NAME_INPUT }}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Start date"
              type="date"
              value={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              inputProps={{ 'data-testid': `${CONTENT_PLAN.DATERANGE}-start` }}
            />
            <TextField
              label="End date"
              type="date"
              value={dateEnd}
              onChange={(e) => setDateEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              inputProps={{ 'data-testid': `${CONTENT_PLAN.DATERANGE}-end` }}
            />
          </Box>

          <FormControl fullWidth>
            <InputLabel>Primary goal</InputLabel>
            <Select
              value={primaryGoal}
              label="Primary goal"
              onChange={(e) => setPrimaryGoal(e.target.value as PrimaryGoal)}
              inputProps={{ 'data-testid': CONTENT_PLAN.GOAL_RADIO }}
            >
              <MenuItem value="awareness">Awareness</MenuItem>
              <MenuItem value="engagement">Engagement</MenuItem>
              <MenuItem value="leads">Leads</MenuItem>
              <MenuItem value="sales">Sales</MenuItem>
            </Select>
          </FormControl>

          <Divider />

          <Box data-testid={CONTENT_PLAN.PLATFORMS_MULTI}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Target platforms
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Defaults come from the author platform config. Weekly volume is the number of slots.
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={(event) => setPlatformMenuAnchor(event.currentTarget)}
                disabled={systemPlatformsLoading || Boolean(systemPlatformsError) || availablePlatforms.length === 0}
                data-testid={CONTENT_PLAN.STRATEGY_ADD_PLATFORM_BUTTON}
              >
                Add platform
              </Button>
              <Menu
                anchorEl={platformMenuAnchor}
                open={Boolean(platformMenuAnchor)}
                onClose={() => setPlatformMenuAnchor(null)}
              >
                {availablePlatforms.map((platform) => (
                  <MenuItem
                    key={platform}
                    onClick={() => addPlatform(platform)}
                    data-testid={CONTENT_PLAN.STRATEGY_ADD_PLATFORM_OPTION}
                    data-platform={platform}
                  >
                    {systemPlatformById.get(String(platform))?.displayName
                      ?? `${String(platform).charAt(0).toUpperCase()}${String(platform).slice(1)}`}
                  </MenuItem>
                ))}
              </Menu>
            </Stack>

            {systemPlatformsError && (
              <Alert severity="error" sx={{ mt: 1.5 }}>
                {systemPlatformsError}
              </Alert>
            )}
            {systemPlatformsLoaded && !systemPlatformsError && systemPlatforms.length === 0 && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                System platform catalog is empty. Seed system_platforms before adding platforms.
              </Alert>
            )}

            {effectivePlatformCadences.length === 0 ? (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                Add at least one platform and one weekly posting slot.
              </Alert>
            ) : (
              <Stack spacing={1.5} sx={{ mt: 1.5 }} data-testid={CONTENT_PLAN.STRATEGY_CADENCE_SECTION}>
                {effectivePlatformCadences.map((cadence) => {
                  const expanded = expandedCadencePlatforms.includes(cadence.platform);
                  return (
                    <Paper
                      key={cadence.platform}
                      variant="outlined"
                      data-testid={CONTENT_PLAN.STRATEGY_CADENCE_PLATFORM_SECTION}
                      data-platform={cadence.platform}
                      sx={{ overflow: 'hidden' }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ p: 1.5, cursor: 'pointer' }}
                        onClick={() => toggleCadencePlatform(cadence.platform)}
                      >
                        <Typography sx={{ fontWeight: 600, textTransform: 'capitalize', flex: 1 }}>
                          {cadence.platform}
                        </Typography>
                        <Chip size="small" variant="outlined" label={`${postsPerWeekFromCadence(cadence)} / week`} />
                        <IconButton
                          size="small"
                          aria-label="Remove platform"
                          data-testid={CONTENT_PLAN.STRATEGY_CADENCE_PLATFORM_REMOVE}
                          onClick={(event) => {
                            event.stopPropagation();
                            removePlatform(cadence.platform);
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label={expanded ? 'Collapse platform cadence' : 'Expand platform cadence'}
                          data-testid={CONTENT_PLAN.STRATEGY_CADENCE_PLATFORM_TOGGLE}
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleCadencePlatform(cadence.platform);
                          }}
                        >
                          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </Stack>
                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 1.5, pt: 0 }}>
                          <PlatformCadenceFields
                            mode="strategy"
                            slots={cadenceToFieldSlots(cadence)}
                            onSlotsChange={(slots) =>
                              updatePlatformCadence(cadence.platform, (current) => withFieldSlots(current, slots))
                            }
                            testIdPrefix={`strategy-cadence-${cadence.platform}`}
                          />
                        </Box>
                      </Collapse>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Box>

          <Divider />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Content mix</InputLabel>
              <Select
                value={contentMixPreset}
                label="Content mix"
                onChange={(e) => setContentMixPreset(e.target.value as ContentMixPreset)}
                inputProps={{ 'data-testid': CONTENT_PLAN.MIX_PRESET }}
              >
                <MenuItem value="80_20">80/20 (recommended)</MenuItem>
                <MenuItem value="4_1_1">4-1-1</MenuItem>
                <MenuItem value="70_20_10">70/20/10</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
            {/* Phase C — Pillar mode removed. Pillars are now a first-class
                entity managed in the Creator tab's Pillars editor (B.3); the
                strategy wizard no longer needs to ask about pillar generation
                policy. */}
          </Box>

          <Divider />

          {/* Wave 2 — goal-quota editor. Pre-fills from SPRINT_TYPE_DEFAULT_GOAL_MIX
              based on user's primary goal. Free-form goal strings supported.
              Output flows into the strategy-create request as a default that
              the planner applies uniformly to all sprints' outputGoalQuotas. */}
          <StrategyGoalQuotaEditor
            primarySprintType={primaryGoalToSprintType(primaryGoal)}
            value={defaultGoalQuotas}
            onChange={setDefaultGoalQuotas}
          />

          <Divider />

          {/* workflow-golden-tower — B2B audience-brief picker. Only shown in B2B mode. */}
          {pipelineMode === 'b2b' && (
            <Box data-testid="audience-brief-picker-section">
              <Typography variant="subtitle2" gutterBottom>
                Audience briefs <Typography component="span" variant="caption" color="text.secondary">— required for B2B draft</Typography>
              </Typography>
              <AudienceBriefMultiPicker
                projectId={projectId}
                value={selectedAudienceBriefRefs}
                onChange={setSelectedAudienceBriefRefs}
              />
              <Box sx={{ mt: 1.5 }}>
                <FormControl size="small">
                  <InputLabel id="audience-coverage-policy-label">Coverage policy</InputLabel>
                  <Select
                    labelId="audience-coverage-policy-label"
                    label="Coverage policy"
                    value={audienceCoveragePolicy}
                    onChange={(e) => setAudienceCoveragePolicy(e.target.value as 'advisory' | 'strict')}
                    inputProps={{ 'data-testid': 'audience-coverage-policy-select' }}
                    sx={{ minWidth: 220 }}
                  >
                    <MenuItem value="advisory">advisory (warn on gaps)</MenuItem>
                    <MenuItem value="strict">strict (block approval on gaps)</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          )}

          {/* CompositeDataPicker — same stack StrategyFormDialog uses.
              Auto-loads audiences / tasks / social proofs / insights from
              Firestore and converts selection paths → sourceDataIds + hydrated. */}
          <Box data-testid="source-data-picker">
            <Typography variant="subtitle2" gutterBottom>Source Data</Typography>
            <CompositeDataPicker
              {...orchestrator.compositeProps}
              pickers={[
                { type: 'audience' as const, label: 'Audiences', enabled: true, selectAll: false },
                { type: 'task' as const, label: 'Tasks', enabled: true, selectAll: false },
                { type: 'socialproof' as const, label: 'Social Proof', enabled: true, selectAll: false },
                { type: 'insights' as const, label: 'Insights', enabled: true, selectAll: false },
              ]}
            />
          </Box>

          <Divider />

          {/* Backlog readiness panel — preflight coverage details when available. */}
          {(readinessCoverage || preflightLoading) && (
            <Box data-testid={CONTENT_PLAN.STRATEGY_PREFLIGHT_PANEL}>
              <Typography variant="subtitle2" gutterBottom>Backlog readiness</Typography>
              {preflightLoading ? (
                <Alert severity="info">Checking backlog coverage…</Alert>
              ) : readinessCoverage?.ok ? (
                <Alert severity="success">
                  Backlog covers 100% of required themes — one-shot planning can assign topics immediately.
                </Alert>
              ) : (
                <Alert severity="warning">
                  Backlog covers ~{Math.round((readinessCoverage?.overallCoverage ?? 0) * 100)}% of required topics.
                  Missing: {(readinessCoverage?.gaps ?? []).map((g) => `${g.themeId} (${g.missingCount})`).join(', ')}.
                </Alert>
              )}
            </Box>
          )}

          {/* One-shot mode is only meaningful in B2C mode. In B2B mode the
              draft/approve gate replaces it: B1 emits the skeleton, the user
              edits on the review screen, then B2 runs autofill + planner. */}
          {pipelineMode === 'b2c' && (
            <Box data-testid="auto-fill-backlog-section">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={oneShotMode}
                    onChange={(e) => setOneShotMode(e.target.checked)}
                    data-testid={CONTENT_PLAN.STRATEGY_ONE_SHOT_MODE}
                  />
                }
                label={
                  estimatedTopicsToGenerate > 0
                    ? `One-shot: fill missing topics and assign slots (~${estimatedTopicsToGenerate} topics)`
                    : 'One-shot: auto-fill missing topics and assign slots'
                }
              />
              {oneShotMode ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                  Stage B will fill any coverage gaps, save generated topics as ready, and schedule assigned topics in this run.
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                  Creates the strategy and sprint scaffold only. Backlog review and slot assignment stay manual.
                </Typography>
              )}
            </Box>
          )}

          <Divider />

          {/* Advanced reactive slot buffer */}
          <Box data-testid="strategy-advanced-section">
            <Box
              component="button"
              type="button"
              onClick={() => setAdvancedExpanded((v) => !v)}
              sx={{
                background: 'none',
                border: 'none',
                p: 0,
                cursor: 'pointer',
                color: 'primary.main',
                fontSize: 13,
              }}
            >
              {advancedExpanded ? '▾' : '▸'} Advanced (slot buffer)
            </Box>
            {advancedExpanded && (
              <Stack spacing={1.5} sx={{ mt: 1.5, pl: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Reactive slot buffer: {reactiveSlotPercent}% of slots stay topic-less for
                    spontaneous content. Default is 0%; raise it when manual slots are needed.
                  </Typography>
                  <Slider
                    value={reactiveSlotPercent}
                    onChange={(_, v) => setReactiveSlotPercent(v as number)}
                    min={0}
                    max={50}
                    step={5}
                    marks={[
                      { value: 0, label: '0%' },
                      { value: 30, label: '30%' },
                      { value: 50, label: '50%' },
                    ]}
                    valueLabelDisplay="auto"
                    sx={{ width: 280 }}
                    data-testid="strategy-reactive-slot-pct"
                  />
                </Box>
              </Stack>
            )}
          </Box>

          <Divider />

          {/* Models */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>AI models (advanced)</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <AvailableModelSelector
                projectId={projectId}
                value={modelStrategy}
                onChange={setModelStrategy}
                label="Strategy agent"
                testId={CONTENT_PLAN.MODEL_STRATEGY}
              />
              <AvailableModelSelector
                projectId={projectId}
                value={modelPlatformPost}
                onChange={setModelPlatformPost}
                label="Platform post agent"
                testId={CONTENT_PLAN.MODEL_PLATFORM_POST}
              />
            </Box>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={creatorResearchEnabled}
                onChange={(e) => setCreatorResearchEnabled(e.target.checked)}
                data-testid={CONTENT_PLAN.CR_ENABLE_CHECK}
              />
            }
            label={
              <>
                Enable Creator Research <Typography component="span" variant="caption" sx={{ color: 'warning.main' }}>(paid — Apify, separate Stage D)</Typography>
              </>
            }
          />

          {/* Manual escape hatch — create a shell strategy without the AI pipeline.
              Bypasses backlog coverage, theme quotas, and all Stage B/C agents.
              Useful when the backlog can't satisfy theme coverage, or the user
              just wants to author the plan manually via the Calendar tab. */}
          <Divider sx={{ mt: 2, mb: 1 }}>or bypass AI</Divider>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Skip AI generation. Creates an empty strategy shell with just name,
              dates, and platforms — you fill sprints & publications manually from
              the Calendar tab.
            </Typography>
            <Box
              component="button"
              type="button"
              onClick={handleCreateBlank}
              data-testid={CONTENT_PLAN.CREATE_BLANK_BTN}
              disabled={!canSubmit || isSubmitting}
              sx={{
                background: 'none',
                border: 1,
                borderColor: 'divider',
                borderStyle: 'dashed',
                borderRadius: 1,
                px: 2,
                py: 1,
                cursor: 'pointer',
                color: 'text.primary',
                fontSize: 13,
                '&:hover:not(:disabled)': { bgcolor: 'action.hover', borderColor: 'text.secondary' },
                '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
              }}
            >
              {isSubmitting ? 'Creating…' : 'Create blank strategy'}
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogFooter
        secondaryAction={{
          label: 'Cancel',
          onClick: onClose,
          'data-testid': CONTENT_PLAN.CANCEL_BTN,
        } as any}
        primaryAction={{
          label: isSubmitting
            ? 'Generating…'
            : pipelineMode === 'b2b'
              ? 'Generate Draft Strategy'
              : 'Generate Strategy',
          onClick: pipelineMode === 'b2b' ? handleSubmitB2BDraft : handleSubmit,
          disabled:
            !canSubmit
            || isSubmitting
            || (pipelineMode === 'b2b' && selectedAudienceBriefRefs.length === 0),
          'data-testid': CONTENT_PLAN.GENERATE_BTN,
        } as any}
      />
    </BaseDialog>
  );
};

/**
 * Wave 2 — map the user-selected `primaryGoal` (`awareness | engagement |
 * leads | sales`) onto a SprintTypeKind so the goal-quota editor can pre-fill
 * from SPRINT_TYPE_DEFAULT_GOAL_MIX. The mapping mirrors the dominant
 * intent of each strategy goal (sales→conversion, leads→conversion,
 * engagement→community, awareness→awareness).
 */
function primaryGoalToSprintType(primary: PrimaryGoal): import('@segmently/schema-models/content-plan').SprintTypeKind {
  switch (primary as string) {
    case 'sales':
    case 'leads':
      return 'conversion' as any;
    case 'engagement':
      return 'community' as any;
    case 'awareness':
    default:
      return 'awareness' as any;
  }
}

export default CreateContentStrategyDialog;
